import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTrackView } from "@/hooks/use-track-view";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { formatCurrency, formatDate, pctChange, centsToDollars, dollarsToCents } from "@/lib/format";
import { useState, useEffect, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, BarChart, Bar, Legend } from "recharts";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";
import { toast } from "sonner";
import { useMoney } from "@/hooks/use-money";
import type { PaceStatus } from "@/lib/money";
import { BackfillPrompt } from "@/components/backfill-prompt";

const FLOOR_COPY =
  "If Stripe or CSV shows more cash landed in a month than your entries add up to, the higher Stripe/CSV amount counts for that month — it's never added on top.";
import { RecurringClientsCard, OneOffRevenueCard } from "@/components/revenue-sources";
import { RevenueMonthBreakdown } from "@/components/revenue-month-breakdown";
import { RevenueSignalsCard } from "@/components/revenue-signals-card";

export const Route = createFileRoute("/_authenticated/revenue")({
  head: () => ({ meta: [
    { title: "Revenue Pulse — Boardroom" },
    { name: "description", content: "Track your revenue, recurring income, and annual goal pacing in Boardroom." },
    { property: "og:title", content: "Revenue Pulse — Boardroom" },
    { property: "og:description", content: "Track your revenue, recurring income, and annual goal pacing in Boardroom." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary_large_image" },
  ] }),
  component: RevenuePage,
});

function RevenuePage() {
  const { user } = useAuth();
  useTrackView("revenue");
  if (!user) return null;
  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-3xl font-semibold">Revenue Pulse</h1>
        <p className="text-sm text-muted-foreground mt-1">One number is the truth. Everything else is a hint.</p>
        <p className="text-xs text-muted-foreground mt-2 max-w-2xl">
          Your total = this month's revenue box + closed-won deals + recurring clients + one-off payments, and if Stripe or CSV shows more cash landed that month, the higher Stripe/CSV amount counts instead (never added on top).
        </p>
      </div>


      <KeyMetrics userId={user.id} />
      <GoalPacing userId={user.id} />
      <MonthInputs userId={user.id} />
      <RevenueSignalsCard userId={user.id} />
      <ProjectionChart userId={user.id} />
      <div className="grid lg:grid-cols-2 gap-6">
        <RecurringClientsCard userId={user.id} />
        <OneOffRevenueCard userId={user.id} />
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2"><MonthlyChart userId={user.id} /></div>
        <SourceBreakdown userId={user.id} />
      </div>
    </div>
  );
}



function MonthInputs({ userId }: { userId: string }) {
  const qc = useQueryClient();
  const now = new Date(); const y = now.getFullYear(); const curM = now.getMonth() + 1;
  const [m, setM] = useState(curM);
  const { data } = useQuery({
    queryKey: ["metric-current", userId, y, m],
    queryFn: async () => {
      const { data } = await supabase.from("monthly_metrics").select("*").eq("user_id", userId).eq("year", y).eq("month", m).maybeSingle();
      return data as any;
    },
  });
  const [manual, setManual] = useState("");
  const [hours, setHours] = useState("");
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setDirty(false); }, [m]);
  // Re-seed whenever the stored amount changes (e.g. after tapping "Use this"
  // on a Stripe/CSV signal), unless the user has unsaved edits in the box.
  useEffect(() => {
    if (dirty) return;
    setManual(data?.manual_revenue_cents ? String(centsToDollars(data.manual_revenue_cents)) : "");
    setHours(data?.hours_worked != null ? String(data.hours_worked) : "");
  }, [data?.id, data?.manual_revenue_cents, data?.manual_source_at, data?.hours_worked, dirty, m]);

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        user_id: userId, year: y, month: m,
        manual_revenue_cents: dollarsToCents(manual || "0"),
        hours_worked: hours.trim() === "" ? null : Number(hours),
        manual_source: null,
        manual_source_at: null,
      };
      const { error } = await supabase.from("monthly_metrics").upsert(payload as any, { onConflict: "user_id,year,month" });
      if (error) throw error;
    },
    onSuccess: () => { setDirty(false); qc.invalidateQueries(); toast.success("Saved as your own number"); },
    onError: (e: any) => toast.error(e.message),
  });

  const clearSource = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("monthly_metrics").upsert({
        user_id: userId, year: y, month: m,
        manual_revenue_cents: data?.manual_revenue_cents ?? 0,
        manual_source: null,
        manual_source_at: null,
      } as any, { onConflict: "user_id,year,month" });
      if (error) throw error;
    },
    onSuccess: () => { setDirty(false); qc.invalidateQueries(); toast.success("Source cleared"); },
    onError: (e: any) => toast.error(e.message),
  });

  const monthName = (mm: number) => new Date(y, mm - 1, 1).toLocaleDateString("en-US", { month: "long" });

  return (
    <div id="month-entry" className="boardroom-card p-5 scroll-mt-24">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-2 flex-wrap">
        <select
          value={m}
          onChange={(e) => setM(Number(e.target.value))}
          className="bg-transparent uppercase tracking-wider text-[11px] text-muted-foreground outline-none cursor-pointer"
          aria-label="Month"
        >
          {Array.from({ length: curM }, (_, i) => i + 1).map((mm) => (
            <option key={mm} value={mm} className="bg-[color:var(--surface-1)]">{monthName(mm)} {y}</option>
          ))}
        </select>
        <span>— Revenue</span>
      </div>
      <div className="text-xs text-muted-foreground mb-3">Log any income for this month not already covered by recurring clients, one-off payments or closed deals. <span className="text-[color:var(--gold)]">{FLOOR_COPY}</span> The chart below shows your track record.</div>
      <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
        <div>
          <div className="grid grid-cols-[1fr_8rem] gap-3">
            <div>
              <Label className="text-xs">{m === curM ? "This month's revenue" : `${monthName(m)} revenue`}</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                <Input type="number" value={manual} onChange={(e) => { setDirty(true); setManual(e.target.value); }} className="pl-7 tabular" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Hours worked</Label>
              <Input type="number" min="0" value={hours} onChange={(e) => { setDirty(true); setHours(e.target.value); }} className="mt-1 tabular" placeholder="—" />
            </div>
          </div>
          <div className="mt-2 flex items-center gap-2 flex-wrap text-[11px]">
            {data?.manual_source ? (
              <>
                <span className="rounded border border-[color:var(--teal)] px-2 py-1 text-[color:var(--teal)]">
                  From {data.manual_source}{data.manual_source_at ? ` · applied ${new Date(data.manual_source_at).toLocaleDateString()}` : ""}
                </span>
                <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={() => clearSource.mutate()} disabled={clearSource.isPending}>
                  Clear source
                </Button>
              </>
            ) : (
              <span className="text-muted-foreground">Entered by you</span>
            )}
          </div>
        </div>
        <Button onClick={() => save.mutate()} disabled={save.isPending} className="bg-[color:var(--gold)] text-[color:var(--primary-foreground)]">Save</Button>
      </div>
    </div>
  );
}

function KeyMetrics({ userId }: { userId: string }) {
  const { data: money } = useMoney(userId);
  const m = money
    ? {
        thisMonthRev: money.thisMonthCents,
        lastMonthRev: money.lastMonthCents,
        yearRev: money.ytdCents,
        ehrCents: money.ehr.cents,
        ehrHours: money.ehr.hours,
        ehrEstimated: money.ehr.estimated,
        ehrMonth: new Date(money.ehr.year, money.ehr.month - 1, 1).toLocaleDateString("en-US", { month: "short" }),
        gap: Math.max(0, money.annualGoalCents - money.ytdCents),
      }
    : { thisMonthRev: 0, lastMonthRev: 0, yearRev: 0, ehrCents: 0, ehrHours: 0, ehrEstimated: true, ehrMonth: "", gap: 0 };
  const change = pctChange(m.thisMonthRev, m.lastMonthRev);
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <Card label="This Month" value={formatCurrency(m.thisMonthRev)} sub={
        <span className={`inline-flex items-center gap-1 ${change >= 0 ? "text-[color:var(--success)]" : "text-[color:var(--danger)]"}`}>
          {change >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
          {Math.abs(change).toFixed(0)}% vs last month
        </span>
      } />
      <Card label="Year to Date" value={formatCurrency(m.yearRev)} sub={<span className="text-muted-foreground">{formatCurrency(m.gap)} from goal</span>} />
      <Card label="Effective Hourly Rate" value={formatCurrency(m.ehrCents)} sub={<span className="text-muted-foreground">{m.ehrMonth} · {m.ehrEstimated ? `estimated from your target hours (${Math.round(m.ehrHours)} hrs)` : `${Math.round(m.ehrHours)} hrs worked`}</span>} />
      <Card label="Last Month" value={formatCurrency(m.lastMonthRev)} sub={<span className="text-muted-foreground">Prior month total</span>} />
    </div>
  );
}

function Card({ label, value, sub }: { label: string; value: string; sub?: React.ReactNode }) {
  return (
    <div className="boardroom-card p-5">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-display text-2xl font-semibold tabular text-[color:var(--gold)] mt-2">{value}</div>
      {sub && <div className="text-xs mt-2 tabular">{sub}</div>}
    </div>
  );
}

function GoalPacing({ userId }: { userId: string }) {
  const { data: money } = useMoney(userId);
  const d = money
    ? {
        ytd: money.ytdCents,
        goal: money.annualGoalCents,
        projected: money.projectedCents,
        pacePct: money.pacePct,
        pctOfGoal: money.pctOfGoal,
        pctOfYear: money.onPaceMarkPct,
        status: money.paceStatus,
        delta: money.projectedCents - money.annualGoalCents,
        needsBackfill: money.needsBackfill,
      }
    : { ytd: 0, goal: 0, projected: 0, pacePct: 0, pctOfGoal: 0, pctOfYear: 0, status: "unknown" as PaceStatus, delta: 0, needsBackfill: false };
  if (!d.goal) {
    return (
      <div className="boardroom-card p-5">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Goal Pacing</div>
        <p className="text-sm text-muted-foreground mt-2">Set an annual revenue goal in Settings to see your pace vs. goal here.</p>
      </div>
    );
  }
  const status = d.status === "ahead"
    ? { label: "Ahead of pace", color: "var(--success)" }
    : d.status === "on_pace"
      ? { label: "On pace", color: "var(--teal)" }
      : { label: "Behind pace", color: "var(--warning)" };
  return (
    <div className="boardroom-card p-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Goal Pacing</div>
        <span
          className="text-[11px] font-medium px-2 py-0.5 rounded-full"
          style={{ backgroundColor: `color-mix(in srgb, ${status.color} 18%, transparent)`, color: status.color }}
        >
          {status.label}
        </span>
      </div>
      <BackfillPrompt show={d.needsBackfill} />
      <div className="mt-3 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs text-muted-foreground">Year to date</div>
          <div className="font-display text-2xl font-semibold tabular text-[color:var(--gold)]">{formatCurrency(d.ytd)}</div>
        </div>
        <div className="text-right">
          <div className="text-xs text-muted-foreground">Projected at current pace</div>
          <div className="font-display text-2xl font-semibold tabular">{formatCurrency(d.projected)}</div>
        </div>
      </div>

      {/* Pace bar: gold = progress toward goal, track shows where "on pace" is */}
      <div className="mt-4">
        <div className="relative h-3 rounded-full bg-[color:var(--hairline)] overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 rounded-full"
            style={{ width: `${d.pctOfGoal}%`, backgroundColor: status.color }}
          />
          {/* on-pace marker = share of the year elapsed since pacing started */}
          <div
            className="absolute inset-y-0 w-px bg-foreground/70"
            style={{ left: `${d.pctOfYear}%` }}
            title="Where you should be today"
          />
        </div>
        <div className="flex items-center justify-between mt-1.5 text-[11px] text-muted-foreground">
          <span>{d.pctOfGoal}% of goal</span>
          <span>on-pace mark: {d.pctOfYear}%</span>
        </div>
      </div>

      <p className="text-xs text-muted-foreground mt-3">
        {d.status === "on_pace" || d.status === "ahead"
          ? `On track to finish near ${formatCurrency(d.projected)}, which is ${d.pacePct}% of your ${formatCurrency(d.goal)} goal.`
          : `You're ${formatCurrency(Math.abs(d.delta))} short of a pace that hits your goal.`}
      </p>
    </div>
  );
}

function MonthlyChart({ userId }: { userId: string }) {
  const [sel, setSel] = useState<{ y: number; m: number } | null>(null);
  const { data: money } = useMoney(userId);
  const data = useMemo(() => {
    if (!money) return [];
    const now = new Date();
    return Array.from({ length: 12 }, (_, idx) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (11 - idx), 1);
      const y = d.getFullYear(); const m = d.getMonth() + 1;
      return { label: d.toLocaleDateString("en-US", { month: "short" }), y, m, revenue: money.monthTotal(y, m) / 100 };
    });
  }, [money]);
  return (
    <div className="boardroom-card p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Monthly Revenue · Last 12 Months</div>
        <div className="text-[11px] text-muted-foreground">Tap a month for breakdown</div>
      </div>
      <div style={{ width: "100%", height: 240 }}>
        <ResponsiveContainer>
          <LineChart
            data={data}
            onClick={(state: any) => {
              const p = state?.activePayload?.[0]?.payload;
              if (p) setSel({ y: p.y, m: p.m });
            }}
          >
            <XAxis dataKey="label" stroke="var(--muted-foreground)" fontSize={11} />
            <YAxis stroke="var(--muted-foreground)" fontSize={11} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
            <Tooltip contentStyle={{ background: "var(--surface-2)", border: "1px solid var(--hairline)", borderRadius: 8, color: "var(--foreground)" }} formatter={(v: number) => `$${v.toLocaleString()}`} />
            <Line type="monotone" dataKey="revenue" stroke="var(--gold)" strokeWidth={2} dot={{ fill: "var(--gold)", r: 3, style: { cursor: "pointer" } }} activeDot={{ r: 5, style: { cursor: "pointer" } }} style={{ cursor: "pointer" }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {sel && (
        <RevenueMonthBreakdown
          userId={userId}
          year={sel.y}
          month={sel.m}
          open={!!sel}
          onOpenChange={(v) => !v && setSel(null)}
        />
      )}
    </div>
  );
}

const SOURCE_COLORS = ["var(--gold)", "var(--teal)", "var(--success)", "var(--warning)", "var(--muted-foreground)"];
function SourceBreakdown({ userId }: { userId: string }) {
  const { data = [] } = useQuery({
    queryKey: ["source-breakdown", userId],
    queryFn: async () => {
      const { data } = await supabase.from("proposals").select("lead_source, value_cents").eq("user_id", userId).eq("stage", "closed_won").eq("exclude_from_revenue", false);
      const map = new Map<string, number>();
      (data ?? []).forEach((p) => map.set(p.lead_source ?? "other", (map.get(p.lead_source ?? "other") ?? 0) + p.value_cents));
      const labels: Record<string, string> = { referral: "Referral", social: "Social", outreach: "Outreach", repeat: "Repeat", other: "Other" };
      return Array.from(map.entries()).map(([k, v]) => ({ name: labels[k] ?? k, value: v / 100 }));
    },
  });
  return (
    <div className="boardroom-card p-5">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-4">Revenue Sources</div>
      {data.length === 0 ? (
        <div className="text-sm text-muted-foreground py-12 text-center">Tag closed-won proposals with a source to see this.</div>
      ) : (
        <div style={{ width: "100%", height: 240 }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                {data.map((_, i) => <Cell key={i} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "var(--surface-2)", border: "1px solid var(--hairline)", borderRadius: 8 }} formatter={(v: number) => `$${v.toLocaleString()}`} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function ProjectionChart({ userId }: { userId: string }) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [selMonth, setSelMonth] = useState<number | null>(null);
  const { data: money } = useMoney(userId);
  const data = useMemo(() => {
    if (!money) return [];
    return money.projectYear(year).map((r) => ({
      label: new Date(year, r.m - 1, 1).toLocaleDateString("en-US", { month: "short" }),
      m: r.m,
      actual: r.actualCents / 100,
      projected: r.projectedCents / 100,
    }));
  }, [money, year]);
  const totalProjected = data.reduce((s: number, r: any) => s + r.actual + r.projected, 0);
  return (
    <div className="boardroom-card p-5">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Yearly Projection</div>
          <div className="text-xs text-muted-foreground mt-1">Gold = actual money in. Teal = projected to December from your recurring clients plus your average project work across your complete months. Tap a bar for that month's breakdown.</div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setYear(year - 1)}>‹</Button>
          <div className="font-display text-lg tabular w-16 text-center">{year}</div>
          <Button variant="ghost" size="sm" onClick={() => setYear(year + 1)}>›</Button>
        </div>
      </div>
      <div style={{ width: "100%", height: 260 }}>
        <ResponsiveContainer>
          <BarChart
            data={data}
            onClick={(state: any) => {
              const p = state?.activePayload?.[0]?.payload;
              if (p?.m) setSelMonth(p.m);
            }}
          >
            <XAxis dataKey="label" stroke="var(--muted-foreground)" fontSize={11} />
            <YAxis stroke="var(--muted-foreground)" fontSize={11} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
            <Tooltip contentStyle={{ background: "var(--surface-2)", border: "1px solid var(--hairline)", borderRadius: 8, color: "var(--foreground)" }} formatter={(v: number) => `$${v.toLocaleString()}`} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="actual" name="Actual" stackId="a" fill="var(--gold)" radius={[0, 0, 0, 0]} style={{ cursor: "pointer" }} />
            <Bar dataKey="projected" name="Projected (recurring)" stackId="a" fill="var(--teal)" fillOpacity={0.6} radius={[4, 4, 0, 0]} style={{ cursor: "pointer" }} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-3 text-xs text-muted-foreground tabular">
        {year} total (actual + projected): <span className="text-[color:var(--gold)] font-medium">${totalProjected.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
      </div>
      {selMonth !== null && (
        <RevenueMonthBreakdown
          userId={userId}
          year={year}
          month={selMonth}
          open={selMonth !== null}
          onOpenChange={(v) => !v && setSelMonth(null)}
        />
      )}
    </div>
  );
}
